// src/app/activities/error.tsx
'use client';

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  React.useEffect(() => {
    console.error('Activities page error:', error);
  }, [error]);

  return (
    <Box sx={{ minHeight: '100vh', background: '#1a1a1a', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="md">
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
            border: '1px solid #444444',
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFD400', 
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' }
            }}
          >
            Something went wrong
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#e0e0e0', 
              mb: 2,
              fontWeight: 500
            }}
          >
            Unable to load Program Activities
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#b0b0b0', 
              mb: 4,
              maxWidth: '500px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            We encountered an issue while loading the program activities. This might be a temporary 
            problem with our servers. Please try refreshing the page.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={reset}
              startIcon={<RefreshIcon />}
              sx={{
                background: 'linear-gradient(45deg, #FFD400, #e6c000)',
                color: '#242424',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(45deg, #e6c000, #FFD400)',
                }
              }}
            >
              Try Again
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => window.location.href = '/'}
              sx={{
                color: '#FFD400',
                borderColor: '#FFD400',
                px: 3,
                py: 1.5,
                '&:hover': {
                  background: 'rgba(255, 212, 0, 0.1)',
                  borderColor: '#FFD400',
                }
              }}
            >
              Go Home
            </Button>
          </Box>
          
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 4, p: 2, background: 'rgba(255, 0, 0, 0.1)', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: '#ff6b6b', fontFamily: 'monospace' }}>
                Development Error: {error.message}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}